/* A panel. White surface, hairline header, and a body that scrolls **inside itself**.
   `min-height: 0` on the body is what makes that work rather than growing the page.

   Structure comes from the panel and its header rule; inside the body, group with space and
   hairlines, never with another box (33-ui-build-spec, Space). One radius: `--radius`, 10px. */

.panel {
  /* Placement comes from a custom property, not an inline `grid-column`. An inline style wins
     against every stylesheet rule, which meant the narrow-width layout below could never take
     effect and two panels stayed side by side at 400px. */
  grid-column: var(--panel-col, auto);
  grid-row: var(--panel-row, auto);
  background: var(--panel);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  /* The padding, size and body padding stay custom properties: the signed-out auth card is a
     standalone card on an empty page, not one of six panels competing for a viewport, and it
     raises all three. The default is the dense one. */
  padding: var(--panel-head-pad, 10px 14px);
  border-bottom: 1px solid var(--line);
  font-weight: 600;
  font-size: var(--panel-head-size, var(--t-dense));
  line-height: 1.3;
  color: var(--ink);
  flex-shrink: 0;
}

.headText {
  min-width: 0;
}

/* The title is a heading element. A browser gives `h1`-`h3` their own size and margin, so both are
   reset: the point is the document's structure, and it must cost no pixel. */
.headTitle {
  margin: 0;
  font: inherit;
  color: inherit;
}

/* Rank 2: muted, one weight lighter, capped measure so a long subtitle wraps rather than pushing
   the header's action off the panel. */
.sub {
  display: block;
  margin-top: 1px;
  font-weight: 400;
  color: var(--muted);
  font-size: var(--t-min);
  max-width: 92ch;
}

.body {
  padding: var(--panel-body-pad, 12px 14px);
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.bodyFlush {
  padding: 0;
}

.action {
  font-size: var(--t-min);
  font-weight: 500;
  color: var(--accent);
  background: var(--accent-w);
  padding: 3px 8px;
  border-radius: var(--radius-ctl);
  border: 0;
  cursor: pointer;
  flex-shrink: 0;
}

.action:hover {
  background: var(--accent-line);
}

/* An empty read, said once and plainly: muted, centred, measure capped. The next action is the
   caller's sentence, so this only has to be quiet enough not to look like an error. */
.empty {
  color: var(--muted);
  font-size: var(--t-dense);
  line-height: 1.5;
  max-width: 52ch;
  margin: 0 auto;
  padding: 24px 0;
  text-align: center;
}

/* One column at phone width, whatever the screen asked for — and the panel stops being its own
   scroller, because the *page* is the scroller here.

   **UX-03.** `AppShell.module.css` already said this: at narrow width the body grid uses
   `grid-auto-rows: auto` and scrolls, "because at this width a fixed-height grid would clip a
   panel rather than let it be read." Only the shell half was written. A panel is a column flex
   box whose body is `flex: 1; min-height: 0` — correct when a grid row hands it a height, and
   with `auto` rows there is no height to hand, so `flex: 1` of nothing collapsed the body to
   zero and every panel became its 54px header with three to nineteen pixels of a list clipped
   underneath it.

   It went unnoticed because `ui-check` asserted no *horizontal* overflow at 400px, which is the
   failure that visibly breaks a layout — and this one leaves a tidy-looking page with the content
   missing. It is the shape 200% zoom arrives as: a 1440x900 screen at 200% is a 720x450 viewport,
   so every keyboard-and-magnification user has been getting the collapsed version. */
@media (max-width: 900px) {
  .panel {
    grid-column: 1 / -1;
    grid-row: auto;
    /* **`display: block`, not `flex` with an auto-sized body.** Releasing the body's scroller on
       its own was not enough: the panel stayed a column flex box, and a flex container that is
       also a stretched grid item in an `auto` row resolved to the header's height and let the body
       paint over the panel below it — visibly worse than the clipping it replaced. Taking the flex
       layout away entirely is what makes the section grow with its children. */
    display: block;
    min-height: auto;
    overflow: visible;
  }

  .body {
    overflow-y: visible;
  }
}

/* And the same when the viewport is merely **short**. A wide screen at high magnification — 2560
   at 200% is 1280x700, a 24-inch at 400% is shorter still — keeps the desktop grid, whose panel
   row is `minmax(0, 1fr)` of whatever height is left. That is legible at 900px and unusable at
   450, and a scroller ten pixels tall is data loss even though nothing is technically hidden. */
@media (max-height: 620px) {
  .panel {
    display: block;
    min-height: auto;
    overflow: visible;
  }

  .body {
    overflow-y: visible;
  }
}
